import msBaseApi from "../api/msBaseApi";
import { CalendarEvent } from "./calendarApi";

// Gränssnitt för delta-svar från Microsoft Graph
export interface CalendarDeltaResponse {
  value: CalendarEvent[];
  "@odata.nextLink"?: string; // Länk för att fortsätta hämta mer data
  "@odata.deltaLink"?: string; // Länk för att få framtida ändringar
}

// Interface för delta-synkronisering parametrar
export interface CalendarDeltaParams {
  deltaToken?: string; // Token från föregående deltaLink
  workItemsOnly?: boolean; // Endast händelser kopplade till workItems
}

// Response för deltaSync
export interface CalendarDeltaResult {
  events: CalendarEvent[];
  deltaLink: string | null;
}

// Response för synkronisering av workItems
export interface WorkItemSyncResult {
  added: CalendarEvent[];
  modified: CalendarEvent[];
  deleted: CalendarEvent[];
  deltaLink: string | null;
}

// Hjälpfunktion för att extrahera workItemNo från händelse
export const getWorkItemNoFromEvent = (event: CalendarEvent): string | null => {
  if (!event.extensions) return null;

  const workItemExt = event.extensions.find(
    (ext) => ext.extensionName === "com.kraftvyn.workitem" && ext.workItemNo
  );

  return workItemExt?.workItemNo || null;
};

// Implementera delta-API för kalender
export const calendarDeltaApi = msBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Hämta kalenderändringar med delta-query
    getCalendarDelta: builder.query<
      CalendarDeltaResult,
      CalendarDeltaParams | void
    >({
      query: (params) => {
        // Om vi har en deltaToken, använd den direkt
        if (params?.deltaToken) {
          return { url: params.deltaToken };
        }

        // För initial anrop, använd delta endpoint utan parametrar
        return {
          url: "/me/calendar/events/delta",
        };
      },
      transformResponse: (response: CalendarDeltaResponse, meta, arg) => {
        let events = response.value || [];

        // Om vi bara vill ha händelser kopplade till workItems, filtrera på klientsidan
        if (arg && "workItemsOnly" in arg && arg.workItemsOnly) {
          events = events.filter((event) => {
            return event.extensions?.some(
              (ext) =>
                ext.extensionName === "com.kraftvyn.workitem" && ext.workItemNo
            );
          });
        }

        return {
          events: events,
          deltaLink: response["@odata.deltaLink"] || null,
        };
      },
      providesTags: ["Calendar"],
    }),

    // Synka workItems med kalenderhändelser
    syncWorkItemCalendarEvents: builder.query<
      WorkItemSyncResult,
      { deltaToken?: string }
    >({
      query: ({ deltaToken }) => {
        // Om vi har en deltaToken, använd den direkt
        if (deltaToken) {
          return { url: deltaToken };
        }

        // För initial anrop, använd delta endpoint utan parametrar
        return {
          url: "/me/calendar/events/delta",
        };
      },
      transformResponse: (response: CalendarDeltaResponse) => {
        // Resultat som ska returneras
        const result: WorkItemSyncResult = {
          added: [],
          modified: [],
          deleted: [],
          deltaLink: response["@odata.deltaLink"] || null,
        };

        // Processa alla händelser från delta-svaret
        for (const event of response.value) {
          // Kontrollera om detta är en workItem-relaterad händelse

          // Kontrollera om händelsen har markerats som borttagen
          if ("@removed" in event) {
            result.deleted.push(event);
          }
          // Om händelsen har workItem-extension, kategorisera som tillagd eller ändrad
          else {
            // Enkel detektering för demo - i verkligheten behövs mer avancerad logik
            result.modified.push(event);
          }
        }

        return result;
      },
      providesTags: ["Calendar"],
    }),
  }),
});

// Exportera hooks för användning i komponenter
export const {
  useGetCalendarDeltaQuery,
  useSyncWorkItemCalendarEventsQuery,
  useLazySyncWorkItemCalendarEventsQuery,
} = calendarDeltaApi;
